/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, Input } from '@angular/core';

import { isNotNil, sortedIndexWith } from '@gh/core-util';

import { clamp, noop } from 'lodash';

import { LeadBoardListItem } from '../../../../resources/lead-board.model';
import { leadBoardItemComparator } from '../../lead-board.state';
import { VirtualItemSizeStrategy } from '../../virtual-rendering';
import { VirtualRange } from '../../virtual-rendering/virtual-rendering.interfaces';

@Directive({
  selector: '[ghLeadCardHeightStrategy]',
  exportAs: 'ghLeadCardHeightStrategy',
})
export class LeadCardHeightStrategyDirective implements VirtualItemSizeStrategy<LeadBoardListItem> {
  @Input() itemHeight: number;
  @Input() openedItemHeight: number;
  @Input() items: LeadBoardListItem[];

  private _opened?: LeadBoardListItem;
  private _openedIndex?: number;

  private onHeightChange: () => void = noop;

  @Input()
  set opened(opened: Maybe<LeadBoardListItem>) {
    this._opened = opened;
    this._openedIndex = void 0;
    this.onHeightChange();
  }

  get opened(): Maybe<LeadBoardListItem> {
    return this._opened;
  }

  get diffHeight(): number {
    return this.openedItemHeight - this.itemHeight;
  }

  get openedIndex(): number {
    const { _openedIndex, _opened } = this;
    if (isNotNil(_openedIndex)) {
      return _openedIndex;
    } else if (_opened) {
      this._openedIndex = sortedIndexWith(this.items, _opened, leadBoardItemComparator);
      return this._openedIndex;
    } else {
      return -1;
    }
  }

  calculateFullSize(items: LeadBoardListItem[]): number {
    return items.length * this.itemHeight + (this._opened ? this.diffHeight : 0);
  }

  calculatePosition(item: LeadBoardListItem, index: number): number {
    if (this._opened) {
      return index * this.itemHeight + (this.openedIndex < index ? this.diffHeight : 0);
    } else {
      return index * this.itemHeight;
    }
  }

  calculateVisibleRange({ start, end }: VirtualRange, items: LeadBoardListItem[]): VirtualRange {
    const { itemHeight } = this;
    return {
      start: clamp(Math.floor(start / itemHeight) - 1, 0, items.length),
      end: clamp(Math.ceil(end / itemHeight) + 2, 0, items.length),
    };
  }

  registerOnSizeChange(fn: () => void): void {
    this.onHeightChange = fn;
  }
}
